import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ProfileMessageDialog from '../portal/ProfileMessageDialog';
import WorkspaceLink from '../workspace/WorkspaceLink';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  AlertTriangle,
  CheckCircle,
  Download,
  FileText,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  Shield,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

function splitBlockers(items) {
  return items.reduce(
    (acc, item) => {
      const severity = String(item.severity || '').toLowerCase();
      if (['info', 'warning', 'soft_warning'].includes(severity)) {
        acc.warnings.push(item);
      } else {
        acc.mustFix.push(item);
      }
      return acc;
    },
    { mustFix: [], warnings: [] },
  );
}

function idPhotoBadgeClasses(key) {
  if (key === 'ready_for_id') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (key === 'supplied') return 'bg-blue-50 text-blue-800 border-blue-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
}

export default function PersonFileHeroCard({
  employeeId,
  employee,
  profileMode = 'employee',
  progressPct = 0,
  completedRequirements = 0,
  totalRequirements = 0,
  readinessLabel = '',
  isReady = false,
  blockers = [],
  onFixIssues,
  onGoToCompliance,
  onRecordExistingEvidence,
  isAuditor = false,
  refreshTrigger = 0,
}) {
  const { token } = useAuth();
  const [packReady, setPackReady] = useState(null);
  const [packLoading, setPackLoading] = useState(false);
  const [divergences, setDivergences] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditChecked, setAuditChecked] = useState(false);
  const [downloadingAuditPack, setDownloadingAuditPack] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [idPhotoStatus, setIdPhotoStatus] = useState(null);

  // Counts come exclusively from the canonical readiness response. The profile
  // must not mix in recruitment-gate copies and inflate apparent tasks.
  const normalizedBlockers = useMemo(
    () => (Array.isArray(blockers) ? blockers : []).map((raw) => (
      typeof raw === 'string' ? { label: raw, severity: 'blocking' } : (raw || {})
    )),
    [blockers],
  );
  const { mustFix } = useMemo(() => splitBlockers(normalizedBlockers), [normalizedBlockers]);
  const syncIssueCount = divergences.length + (packReady === false && !packLoading ? 1 : 0);
  const hasPriorityIssues = mustFix.length > 0 || syncIssueCount > 0;
  // Only open the blocker wizard when canonical blockers actually exist.
  // An unrelated agreement diagnostic must not open an empty, misleading task list.
  const canUseFixWizard = !isAuditor && mustFix.length > 0;

  useEffect(() => {
    if (!employeeId || !token || isAuditor) return undefined;
    let cancelled = false;
    setPackLoading(true);
    axios
      .get(`${API}/employees/${employeeId}/employment-pack/readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setPackReady(Boolean(res.data?.readiness?.ready));
      })
      .catch(() => {
        if (!cancelled) setPackReady(null);
      })
      .finally(() => {
        if (!cancelled) setPackLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, token, isAuditor, refreshTrigger]);

  useEffect(() => {
    // Staff ID photos are issued after recruitment approval; a screening
    // applicant must not be shown an irrelevant 'ID photo missing' warning.
    if (!employeeId || !token || isAuditor || profileMode === 'applicant') {
      setIdPhotoStatus(null);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`${API}/employees/${employeeId}/staff-id`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setIdPhotoStatus(res.data?.photo_status || null);
      })
      .catch(() => {
        if (!cancelled) setIdPhotoStatus(null);
      });

    return () => {
      cancelled = true;
    };
  }, [employeeId, token, isAuditor, profileMode, refreshTrigger, employee?.profile_photo_url, employee?.status, employee?.recruitment_approved]);

  useEffect(() => {
    // Agreement reconciliation is an expensive diagnostic, not canonical
    // readiness. Reset its local display when the person changes, but do not
    // run the audit automatically during first render.
    setDivergences([]);
    setAuditChecked(false);
    setAuditLoading(false);
  }, [employeeId, refreshTrigger]);

  const handleCheckAgreementSync = async () => {
    if (!employeeId || !token || isAuditor || auditLoading) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/admin/agreement-audit/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setDivergences(res.data?.divergence_summary?.divergences || []);
      setAuditChecked(true);
    } catch {
      // Do not claim "in sync" when the diagnostic itself failed.
      setDivergences([]);
      setAuditChecked(false);
      toast.error('Agreement consistency check could not be completed');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDownloadAuditPack = async () => {
    if (!employeeId || !token) return;
    setDownloadingAuditPack(true);
    try {
      const response = await axios.get(
        `${API}/employees/${employeeId}/recruitment-audit-pack/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeName = `${employee?.first_name || 'employee'}_${employee?.last_name || 'file'}`.replace(/\s+/g, '_');
      link.href = url;
      link.download = `recruitment_audit_pack_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Recruitment audit pack downloaded');
    } catch {
      toast.error('Failed to download recruitment audit pack');
    } finally {
      setDownloadingAuditPack(false);
    }
  };

  const headline = isReady
    ? (profileMode === 'applicant' ? 'Ready for recruitment approval' : 'Ready for work')
    : (profileMode === 'applicant' ? 'Not yet ready for recruitment approval' : 'Not yet ready for work');

  const tone = isReady ? 'green' : hasPriorityIssues ? 'amber' : 'slate';
  const toneClasses = {
    green: {
      panel: 'border-emerald-200 bg-emerald-50',
      icon: 'bg-emerald-100 text-emerald-700',
      title: 'text-emerald-950',
      body: 'text-emerald-700',
      progress: '[&>div]:bg-emerald-500',
    },
    amber: {
      panel: 'border-amber-200 bg-amber-50',
      icon: 'bg-amber-100 text-amber-700',
      title: 'text-amber-950',
      body: 'text-amber-700',
      progress: '[&>div]:bg-amber-500',
    },
    slate: {
      panel: 'border-slate-200 bg-slate-50',
      icon: 'bg-slate-100 text-slate-700',
      title: 'text-slate-950',
      body: 'text-slate-600',
      progress: '[&>div]:bg-slate-500',
    },
  }[tone];

  // Avoid turning a broad approval gate into a misleading per-stage task
  // count. The canonical blocker details remain available in the task assistant.
  const statusCopy = isReady
    ? 'No outstanding checks are currently reported.'
    : profileMode === 'applicant'
      ? 'Approval is pending. Each stage shows whether evidence is awaited or a staff review is available.'
      : 'Work clearance is pending. Open the stage records for the next genuine action.';

  // Never advertise a check as immediately actionable until the matching
  // evidence and employer-review steps actually permit it. The stage owns truth.
  const primaryActionLabel = profileMode === 'applicant' ? 'Open Recruitment Checks' : 'Open compliance file';
  const applicantProgressAvailable = profileMode !== 'applicant' || (
    Number.isFinite(progressPct) &&
    Number.isFinite(completedRequirements) &&
    Number.isFinite(totalRequirements) &&
    totalRequirements > 0 &&
    completedRequirements >= 0 &&
    completedRequirements <= totalRequirements
  );

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
      data-testid="person-file-hero"
      aria-label="Recruitment command centre"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {profileMode !== 'applicant' && !isAuditor && idPhotoStatus?.label && (
            <Badge
              variant="outline"
              className={idPhotoBadgeClasses(idPhotoStatus.key)}
              data-testid="admin-id-photo-status"
              title="Staff ID photo status only — this does not indicate work readiness"
            >
              ID photo: {idPhotoStatus.label}
            </Badge>
          )}
          {divergences.length > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Agreement sync issue
            </Badge>
          )}
          {auditChecked && !auditLoading && divergences.length === 0 && packReady !== false && (
            <Badge className="bg-green-600 hover:bg-green-600 gap-1">
              <CheckCircle className="h-3 w-3" />
              Agreement check passed
            </Badge>
          )}
        </div>

        {!isAuditor && (
          <Button
            type="button"
            size="sm"
            onClick={() => setMessageOpen(true)}
            className="gap-1.5"
            data-testid="profile-message-button"
          >
            <MessageCircle className="h-4 w-4" />
            Message
          </Button>
        )}
      </div>

      <div className={`rounded-xl border ${toneClasses.panel} p-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${toneClasses.icon}`}>
              {isReady ? <ShieldCheck className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Recruitment overview
              </p>
              <h2 className={`mt-1 font-heading text-xl font-bold ${toneClasses.title}`}>
                {headline}
              </h2>
              <p className={`mt-1 text-sm ${toneClasses.body}`}>{statusCopy}</p>
              {profileMode !== 'applicant' && readinessLabel && readinessLabel.trim().toLowerCase() !== headline.trim().toLowerCase() && (
                <p className="mt-2 text-xs font-medium text-slate-500">{readinessLabel}</p>
              )}
            </div>
          </div>

          <div className="w-full lg:w-60 shrink-0">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-xs text-slate-500">
                  {profileMode === 'applicant' ? 'Applicant progress' : 'File completion'}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {applicantProgressAvailable ? `${Math.round(progressPct)}%` : '—'}
                </p>
              </div>
              {applicantProgressAvailable ? (
                <Badge variant="outline" className="bg-white text-slate-700">
                  {completedRequirements} / {totalRequirements || '-'}{profileMode === 'applicant' ? ' stages' : ''}
                </Badge>
              ) : (
                <span className="text-xs text-slate-600">Unavailable</span>
              )}
            </div>
            {applicantProgressAvailable ? (
              <Progress value={progressPct} className={`h-2 mt-2 ${toneClasses.progress}`} />
            ) : (
              <p className="mt-2 text-xs text-slate-600">Could not refresh applicant progress. No completion is assumed.</p>
            )}
            {profileMode === 'applicant' && applicantProgressAvailable && (
              <p className="mt-1 text-[11px] text-slate-500">
                Completed/submitted journey stages, including later onboarding. Not recruitment approval.
              </p>
            )}
          </div>
        </div>
      </div>

      {divergences.length > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFixIssues?.();
          }}
          className="mt-4 w-full text-left rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 hover:bg-red-100/80 transition-colors"
        >
          <p className="font-medium flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" />
            Admin and worker views disagree on contract/handbook state
          </p>
          <p className="text-xs mt-1 text-red-700">
            {divergences[0]?.impact || 'Open Fix issues to repair agreements.'}
          </p>
        </button>
      )}

      {/* One navigation entry; the canonical stage workflow explains who can act now. */}
      <div className="mt-3 flex items-center justify-between gap-3" data-testid="person-file-next-action">
        <p className="text-xs text-slate-600">
          Evidence requests, reviews and recorded decisions remain in their original stages.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onGoToCompliance}
          className="shrink-0"
          data-testid="person-file-primary-action"
        >
          {primaryActionLabel}
        </Button>
      </div>

      <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50/50" data-testid="person-file-more-tools">
        <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-slate-700">
          Records, evidence &amp; audit tools
        </summary>
        <div className="flex flex-wrap gap-2 border-t border-slate-200 p-3">
          {canUseFixWizard && (
            <Button type="button" size="sm" variant="outline" onClick={onFixIssues}>
              <FileText className="mr-1 h-3.5 w-3.5" /> Review full blocker list
            </Button>
          )}
          {!isAuditor && onRecordExistingEvidence && (
            <Button type="button" size="sm" variant="outline" onClick={onRecordExistingEvidence}
              data-testid="assisted-onboarding-open">
              Record existing evidence
            </Button>
          )}
          {profileMode !== 'applicant' && !isAuditor && (
            <Button asChild size="sm" variant="outline" data-testid="staff-communications-link">
              <WorkspaceLink to={`/portal/employees/${employeeId}/communications`}>
                <Mail className="mr-1 h-3.5 w-3.5" /> Staff communications
              </WorkspaceLink>
            </Button>
          )}
          {!isAuditor && (
            <Button type="button" size="sm" variant="outline" onClick={handleCheckAgreementSync}
              disabled={auditLoading} data-testid="check-agreement-sync">
              <RefreshCw className={`mr-1 h-3.5 w-3.5 ${auditLoading ? 'animate-spin' : ''}`} />
              {auditLoading ? 'Checking…' : auditChecked ? 'Recheck agreement sync' : 'Check agreement sync'}
            </Button>
          )}
          {!isAuditor && (
            <Button type="button" size="sm" variant="outline" onClick={handleDownloadAuditPack}
              disabled={downloadingAuditPack} data-testid="download-recruitment-audit-pack">
              {downloadingAuditPack ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}
              Audit pack PDF
            </Button>
          )}
          {packLoading && <p className="text-xs text-slate-500">Checking employment pack…</p>}
          {packReady === false && !packLoading && (
            <p className="w-full text-xs text-amber-800">Employment pack still requires review before approval.</p>
          )}
        </div>
      </details>
      {!isAuditor && (
        <ProfileMessageDialog
          open={messageOpen}
          onOpenChange={setMessageOpen}
          employeeId={employeeId}
          employee={employee}
          profileMode={profileMode}
        />
      )}
    </section>
  );
}
